import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';

import { Shape2Icon } from '../../components/Shape2/Shape2';
import {
    Banner,
    CampaignType,
    GdGroupStatus,
    GroupBaseInfo,
    GroupType
} from '../../typings/domain';
import {
    CopyAdGroupsMutation_copyAdGroups_validationResult,
    GdAdGroupModerationStatus,
    GdAdGroupPrimaryStatus,
    GdAdGroupPrimaryStatusDesc,
    GdGroupBlGenerationStatus
} from '../../typings/graphql-grid';
import { DuplicateValidationResults, GroupId } from '../../typings/groups';
import {
    ConditionsKeys,
    CpmGroupShowConditionType,
    DataForEditGroups,
    EditGroupData,
    EditGroupDataValue,
    EditGroupKeywords,
    EditInternalGroupDataValue,
    ProductsList,
    ProductsListItemRestrictionCondition
} from '../../typings/groupsEditState';
import { getIconNameForCampaignType } from '../CampaignHelpers/CampaignHelpers';
import { mapPhraseWithMinusWordsToString } from '../PhrasesHelpers/PhrasesHelpers';

import * as keyset from './GroupsHelpers.i18n';

const i18n = i18nFactory(keyset);

const GROUP_TYPE_CHOOSER_TITLES = {
    [GroupType.CPM_BANNER]: i18n('group-type-chooser-banners'),
    [GroupType.CPM_AUDIO]: i18n('group-type-chooser-audio'),
    [GroupType.CPM_VIDEO]: i18n('group-type-chooser-video'),
    [GroupType.CPM_GEOPRODUCT]: i18n('group-type-chooser-geoproduct'),
    [GroupType.CPM_INDOOR]: i18n('group-type-chooser-indoor-1'),
    [GroupType.CPM_OUTDOOR]: i18n('group-type-chooser-outdoor')
};

export class GroupsHelpers {
    public static getIconNameForGroupType(
        type: GroupType,
        campaignType: CampaignType
    ): Shape2Icon {
        switch (type) {
            case GroupType.TEXT:
                return Shape2Icon.typeText;
            case GroupType.PERFORMANCE:
                return Shape2Icon.typePerformance;
            case GroupType.DYNAMIC:
                return Shape2Icon.typeDynamic;
            case GroupType.MOBILE_CONTENT:
                return Shape2Icon.typeMobileContent;
            case GroupType.MCBANNER:
                return Shape2Icon.typeMCBanner;
            case GroupType.CPM_BANNER:
                return Shape2Icon.typeCPMBanner;
            case GroupType.CPM_VIDEO:
                return Shape2Icon.typeCPCVideo;
            case GroupType.CPM_INDOOR:
                return Shape2Icon.typeCPMIndoor;
            case GroupType.CPM_OUTDOOR:
                return Shape2Icon.typeCPMOutdoor;
            case GroupType.CPM_AUDIO:
                return Shape2Icon.typeCPMAudio;
            case GroupType.CPM_GEOPRODUCT:
                return Shape2Icon.typeCPMBannerInMetro;
            case GroupType.CPM_YNDX_FRONTPAGE:
                return Shape2Icon.typeCPMCampaign;
            case GroupType.INTERNAL:
                return getIconNameForCampaignType(campaignType);
            default:
                throw new Error(
                    `Для группы с типом ${type} не определена иконка`
                );
        }
    }

    public static getTitleForGroupType(
        type: GroupType,
        campaignType: CampaignType
    ): string {
        switch (type) {
            case GroupType.TEXT:
                return i18n('group-type-text');
            case GroupType.PERFORMANCE:
                return i18n('banner-type-performance');
            case GroupType.DYNAMIC:
                return i18n('banner-type-dynamic');
            case GroupType.MOBILE_CONTENT:
                return i18n('banner-type-text-mobile');
            case GroupType.MCBANNER:
                return i18n('banner-type-mcbanner');
            case GroupType.CPM_BANNER:
                return i18n('banner-type-cpm_banner');
            case GroupType.CPM_VIDEO:
                return i18n('banner-type-cpm_video');
            case GroupType.CPM_INDOOR:
                return i18n('banner-type-cpm-indoor-1');
            case GroupType.CPM_OUTDOOR:
                return i18n('banner-type-cpm-outdoor');
            case GroupType.CPM_AUDIO:
                return i18n('banner-type-cpm_audio');
            case GroupType.CPM_GEOPRODUCT:
                return i18n('banner-type-cpm_geoproduct');
            case GroupType.INTERNAL:
                switch (campaignType) {
                    case CampaignType.INTERNAL_DISTRIB:
                        return i18n('banner-type-internal-distrib');
                    case CampaignType.INTERNAL_FREE:
                        return i18n('banner-type-internal-free');
                    default:
                        throw new Error(
                            `Для кампании с типом ${type} не определён тайтл для групп внутренней рекламы`
                        );
                }
            default:
                throw new Error(`Для значения ${type} не определено название`);
        }
    }

    public static getFetchFlags(
        groupsForEditData: DataForEditGroups,
        productsList: ProductsList
    ): {
        fetchOutdoorPlacements: boolean;
        fetchIndoorPlacements: boolean;
        fetchCrypta: boolean;
    } {
        if (!productsList[CampaignType.CPM_BANNER]) {
            throw new Error('Нет рестрикшенов для данной группы');
        }

        const groupList = Object.keys(groupsForEditData.groups).map(
            (key: string) => groupsForEditData.groups[key]
        );

        const restrictions = productsList[
            CampaignType.CPM_BANNER
        ].restrictions.reduce((acc, cur) => {
            acc[cur.id] = cur;
            return acc;
        }, {});

        const actualRestrictions = groupList
            .map(
                ({ value }) =>
                    restrictions[
                        'productRestrictionId' in value
                            ? value.productRestrictionId
                            : ''
                    ]
            )
            .filter(Boolean);

        const actualConditions = actualRestrictions.reduce(
            (acc, { conditions }) => acc.concat(conditions),
            [] as ProductsListItemRestrictionCondition[]
        );

        return {
            fetchOutdoorPlacements: actualConditions.some(
                ({ name }: ProductsListItemRestrictionCondition) =>
                    name === ConditionsKeys.advert_pages_outdoor
            ),
            fetchCrypta: actualRestrictions.some(
                ({ showConditionType }) =>
                    showConditionType === CpmGroupShowConditionType.USER_PROFILE
            ),
            fetchIndoorPlacements: actualConditions.some(
                ({ name }: ProductsListItemRestrictionCondition) =>
                    name === ConditionsKeys.advert_pages_indoor
            )
        };
    }

    public static getTitleForGroupTypeChooser(type: GroupType): string {
        if (!GROUP_TYPE_CHOOSER_TITLES[type]) {
            throw new Error(`Для значения ${type} не определено название`);
        }

        return GROUP_TYPE_CHOOSER_TITLES[type];
    }

    public static getGroupEditPageItemEditorId(groupId: string): string {
        return `group-${groupId}`;
    }

    // Для действий над группами, сейчас необходимо передавать cid (id компании)
    // особенность перловой ручки
    public static getFirstCampaignIdForGroups(
        selection: Array<string>,
        gridData: Array<GroupBaseInfo>
    ): string {
        if (!gridData || !gridData.length) {
            throw new Error('Нет ни одной группы');
        }

        const gridDataObj = gridData.reduce((acc, next) => {
            if (next !== undefined) {
                acc[next.id] = next;
            }

            return acc;
        }, {});

        let resultItem: GroupBaseInfo | undefined;
        for (let i = 0; i < selection.length; i++) {
            resultItem = gridDataObj[selection[i]];
            if (resultItem) {
                break;
            }
        }

        return resultItem ? resultItem.campaign.id : gridData[0].campaign.id;
    }

    // Для действий над баннерами, сейчас необходимо передавать cid и adgroup_ids (id компании и id группы)
    // особенность перловой ручки
    public static getFirstCampaignAndGroupIdsForBanners(
        selection: Array<string>,
        gridData: Array<Banner>
    ): {
        campaignId: string;
        groupId: string;
    } {
        if (!gridData || !gridData.length) {
            throw new Error('Нет ни одной группы');
        }

        const gridDataObj = gridData.reduce((acc, next) => {
            acc[next.id] = next;
            return acc;
        }, {});

        let resultItem: Banner | undefined;
        for (let i = 0; i < selection.length; i++) {
            resultItem = gridDataObj[selection[i]];
            if (resultItem) {
                break;
            }
        }

        return resultItem
            ? {
                  campaignId: resultItem.group.campaign.id,
                  groupId: resultItem.group.id
              }
            : {
                  campaignId: gridData[0].group.campaign.id,
                  groupId: gridData[0].group.id
              };
    }

    public static getTitleForGroupStatus(
        status: GdAdGroupPrimaryStatus | GdAdGroupModerationStatus
    ): string {
        const dictionary = {
            [GdAdGroupPrimaryStatus.ACTIVE]: i18n('active-status'),
            [GdAdGroupPrimaryStatus.ARCHIVED]: i18n('archived-status'),
            [GdAdGroupPrimaryStatus.DRAFT]: i18n('draft-status'),
            [GdAdGroupPrimaryStatus.STOPPED]: i18n('stopped-status'),
            [GdAdGroupPrimaryStatus.MODERATION]: i18n('moderation-status'),
            [GdAdGroupPrimaryStatus.REJECTED]: i18n('rejected-status'),
            [GdAdGroupModerationStatus.ACCEPTED]: i18n('accepted-status'),
            [GdAdGroupModerationStatus.PREACCEPTED]: i18n('preaccepted-status')
        };

        if (!dictionary[status]) {
            throw new Error(`Для значения ${status} не определено название`);
        }

        return dictionary[status];
    }

    public static getHintForGroupStatus(
        campaignType: CampaignType,
        status: GdGroupStatus
    ): string | undefined {
        const { primaryStatus, primaryStatusDesc, blGenerationStatus } = status;

        let primaryStatusHint;
        let blGenerationStatusHint;

        if (campaignType === CampaignType.DYNAMIC) {
            blGenerationStatusHint = GroupsHelpers.getBlGenerationStatusHint(
                primaryStatus,
                blGenerationStatus
            );
        }

        primaryStatusHint = GroupsHelpers.getPrimaryStatusHint(
            primaryStatusDesc
        );

        return (
            compact([primaryStatusHint, blGenerationStatusHint]).join(', ') ||
            undefined
        );
    }

    public static parseGroupsCopyMutationsError(
        validationResult: CopyAdGroupsMutation_copyAdGroups_validationResult
    ): DuplicateValidationResults {
        const { errors } = validationResult;
        const onlyNumbersRegExp = /[^0-9]/g;
        const getAdGroupIds = /(adGroupIds\[\d*?\]).*/g;

        return errors.reduce(
            (acc, next) => {
                if (
                    next.code ===
                    'AdGroupDefectIds.Number.MAX_ADGROUPS_IN_CAMPAIGN'
                ) {
                    acc.copyErrorLimitIdxList.push(
                        next.path.replace(onlyNumbersRegExp, '')
                    );
                } else {
                    acc.copyErrorsBrokenGroupsIdxList.push(
                        next.path
                            .replace(getAdGroupIds, '$1')
                            .replace(onlyNumbersRegExp, '')
                    );
                }

                return acc;
            },
            {
                copyErrorLimitIdxList: [],
                copyErrorsBrokenGroupsIdxList: []
            } as DuplicateValidationResults
        );
    }

    public static parsePhrasesFromInputString(
        inputValue: string
    ): Array<{ phrase: string; minusWords: Array<string> }> {
        return inputValue
            .split(/[,\n]/)
            .reduce(
                (
                    res: Array<{ phrase: string; minusWords: Array<string> }>,
                    phraseString: string
                ) => {
                    const trimmedPhrase = phraseString.trim();

                    if (trimmedPhrase !== '') {
                        const [phrase, ...minusWords] = trimmedPhrase.split(
                            ' -'
                        );

                        res.push({
                            phrase,
                            minusWords: minusWords.map(word => word.trim())
                        });
                    }

                    return res;
                },
                []
            );
    }

    public static getInputStringFromPhrasesList = (
        keywordsData: Array<EditGroupKeywords>
    ): string => {
        if (keywordsData.length === 0) {
            return '';
        }

        return keywordsData
            .map(({ phrase, minusWords }) => {
                return mapPhraseWithMinusWordsToString(phrase, minusWords);
            })
            .join('\n');
    };

    public static getEditGroupInternalDataValue(
        value: EditGroupDataValue
    ): EditInternalGroupDataValue | undefined {
        switch (value.campaignType) {
            case CampaignType.INTERNAL_FREE:
            case CampaignType.INTERNAL_DISTRIB:
                return value;
            default:
                return;
        }
    }

    public static markEditGroupsAsSaved(
        groupsData: Record<GroupId, EditGroupData>,
        updatedGroupIds: Array<GroupId>
    ): Record<GroupId, EditGroupData> {
        return Object.keys(groupsData).reduce((acc, groupId) => {
            const groupData = groupsData[groupId];
            if (!updatedGroupIds.includes(groupId)) {
                return {
                    ...acc,
                    [groupId]: groupData
                };
            }

            return {
                ...acc,
                [groupId]: {
                    ...groupData,
                    serverValue: {
                        ...groupData.value
                    }
                }
            };
        }, {});
    }

    public static getFullyAvailableGroupTypes = (): Array<CampaignType> => {
        return [CampaignType.TEXT];
    };

    public static getFullyAvailableCampaignTypesForBannerCreation = (): Array<
        CampaignType
    > => {
        return [CampaignType.TEXT];
    };

    public static getCpmGroupTypesOrderForGroupCreation = (): GroupType[] => {
        return [
            GroupType.CPM_BANNER,
            GroupType.CPM_VIDEO,
            GroupType.CPM_OUTDOOR,
            GroupType.CPM_GEOPRODUCT,
            GroupType.CPM_INDOOR,
            GroupType.CPM_AUDIO
        ];
    };

    public static getConditionsKeyFromShowConditionType(
        type: CpmGroupShowConditionType
    ): ConditionsKeys {
        const dictionary = {
            [CpmGroupShowConditionType.KEYWORD]: ConditionsKeys.phrases,
            [CpmGroupShowConditionType.USER_PROFILE]: ConditionsKeys.social_demo
        };

        if (!dictionary[type]) {
            throw new Error(
                `Для условия показа ${type} не определено значение`
            );
        }

        return dictionary[type];
    }

    private static getPrimaryStatusHint(
        statusDescription?: GdAdGroupPrimaryStatusDesc
    ): string | undefined {
        const dictionary = {
            [GdAdGroupPrimaryStatusDesc.PREVIOUS_VERSION_SHOWING]: i18n(
                'previous-version-showing-status-desc'
            ),
            [GdAdGroupPrimaryStatusDesc.SHOWING]: i18n('showing-status-desc')
        };

        if (statusDescription) {
            return dictionary[statusDescription];
        }

        return;
    }

    private static getBlGenerationStatusHint(
        status: GdAdGroupPrimaryStatus,
        blGenerationStatus?: GdGroupBlGenerationStatus
    ): string | undefined {
        const dictionary = {
            [GdGroupBlGenerationStatus.NO]: i18n('no-status'),
            [GdGroupBlGenerationStatus.PROCESSING]: i18n('processing-status')
        };

        if (status === GdAdGroupPrimaryStatus.ACTIVE && blGenerationStatus) {
            return dictionary[blGenerationStatus];
        }

        return;
    }
}
